import { AndroidLogo, AppleLogo, ChartBar } from "@phosphor-icons/react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Badge, Button } from "flowbite-react";
import { Megaphone } from "@phosphor-icons/react";
import CountryDisplay from "../../../components/CountryDisplay";
import { AdmobApp, IBidEntry } from "../interface";
import AppLogo from "./AppLogo";
import { Info, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Tooltip } from "flowbite-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CAMPAIGN_STATUS } from "../../../shared/constants";
import { formatNumber, getNetworkWiseRevenue } from "../../../lib/utils";

// Separate component for bid data cell to use useState
const BidDataCell = ({
  bidData,
  t,
}: {
  bidData: IBidEntry[];
  t: (key: string) => string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!bidData || bidData.length === 0) {
    return null;
  }

  const maxVisibleEntries = 2;
  const visibleBids = bidData.slice(0, maxVisibleEntries);
  const remainingCount = bidData.length - maxVisibleEntries;

  const renderBidEntry = (bid: IBidEntry, index: number) => {
    if (bid.campaign_type === "TARGET_CPA") {
      const oldBid = bid.old_bid.toFixed(2);
      const currentBid = bid.current_bid.toFixed(2);

      const textColor =
        bid.campaign_status === CAMPAIGN_STATUS.ENABLED
          ? "text-gray-900"
          : "text-gray-400";

      if (oldBid === currentBid) {
        return (
          <div
            key={index}
            className={`${textColor} relative flex items-center gap-1`}
          >
            {currentBid}
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        );
      }

      return (
        <div key={index} className={`flex items-center gap-1`}>
          <div className={textColor}>{oldBid}</div>
          <div
            className={
              bid.current_bid > bid.old_bid ? "text-green-500" : "text-red-500"
            }
          >
            {bid.current_bid > bid.old_bid ? "↑" : "↓"}
          </div>
          <div
            className={`font-medium ${textColor} relative flex items-center gap-1`}
          >
            {currentBid}
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        </div>
      );
    } else if (
      bid.campaign_type === "TARGET_ROAS" &&
      bid.troas !== undefined &&
      bid.old_troas !== undefined
    ) {
      const textColor =
        bid.campaign_status === CAMPAIGN_STATUS.ENABLED
          ? "text-gray-900"
          : "text-gray-500";

      if (bid.troas === bid.old_troas) {
        return (
          <div
            key={index}
            className={`${textColor} flex relative items-center gap-1`}
          >
            {bid.troas}%
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        );
      }

      return (
        <div key={index} className={`flex items-center gap-1`}>
          <div className={textColor}>{bid.old_troas}%</div>
          <div
            className={
              bid.troas > bid.old_troas ? "text-green-500" : "text-red-500"
            }
          >
            {bid.troas > bid.old_troas ? "↑" : "↓"}
          </div>
          <div
            className={`font-medium ${textColor} relative flex items-center gap-1`}
          >
            {bid.troas}%
            {bid.remarks && (
              <Tooltip content={bid.remarks} placement="top">
                <Info
                  weight="duotone"
                  size={18}
                  className="text-gray-400 cursor-help ml-1"
                />
              </Tooltip>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col space-y-2">
      {/* Always show first 2 entries */}
      {visibleBids.map((bid: IBidEntry, index: number) =>
        renderBidEntry(bid, index)
      )}

      {/* Show expanded entries when expanded */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
              height: { duration: 0.3, ease: "easeInOut" },
              opacity: { duration: 0.2, ease: "easeInOut" },
            }}
            className="overflow-hidden"
          >
            <div className="space-y-2">
              {bidData
                .slice(maxVisibleEntries)
                .map((bid: IBidEntry, index: number) =>
                  renderBidEntry(bid, index + maxVisibleEntries)
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expandable indicator */}
      {remainingCount > 0 && (
        <div
          className="text-xs text-gray-500 font-medium flex items-center gap-1 cursor-pointer hover:text-gray-700 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </motion.div>
          {isExpanded
            ? t("common.showLess")
            : `+${remainingCount} ${t("common.more")}`}
        </div>
      )}
    </div>
  );
};

interface UseTableColumnsProps {
  t: (key: string) => string;
  onCampaignCostClick: (
    countryCode: string,
    countryName: string,
    appPackageId: string
  ) => void;
  navigate: (path: string) => void;
  hasCampaignBidManagementPermission: boolean;
  hasCountryCostReadPermission: boolean;
  hasAppDetailsReadPermission: boolean;
  enabledColumnIds: string[];
  hasAppChartReadPermission: boolean;
  onChartClick: (app: AdmobApp) => void;
  columnIds: { [key: string]: string };
  growthIndicatorRange: number[];
}

export const useTableColumns = ({
  t,
  onCampaignCostClick,
  navigate,
  hasCampaignBidManagementPermission,
  hasCountryCostReadPermission,
  hasAppDetailsReadPermission,
  enabledColumnIds,
  hasAppChartReadPermission,
  onChartClick,
  columnIds,
  growthIndicatorRange,
}: UseTableColumnsProps): ColumnDef<AdmobApp>[] => {
  const clickableClassesForApp = hasAppDetailsReadPermission
    ? "cursor-pointer transition-colors ease hover:text-primary-600"
    : "";

  // All columns, including withTax columns
  const allColumns: ColumnDef<AdmobApp>[] = [
    {
      accessorKey: columnIds.APP_NAME,
      id: columnIds.APP_NAME,
      header: t("appCountryWiseReports.columns.name"),
      size: 280,
      minSize: 100,
      maxSize: 280,
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div
            className={`flex items-center relative gap-2 sm:gap-3 ${clickableClassesForApp}`}
            onClick={
              clickableClassesForApp
                ? () =>
                  navigate(
                    `/app-country-reports/${app.app_slug}?account=${app.user_name}`
                  )
                : undefined
            }
          >
            <div>
              {app.platform.toLowerCase() === "android" ? (
                <AndroidLogo weight="fill" className="w-4 h-4 text-green-400" />
              ) : app.platform.toLowerCase() === "ios" ? (
                <AppleLogo weight="fill" className="w-4 h-4 text-gray-700" />
              ) : null}
            </div>
            <div className="min-w-10 w-10 min-h-10 h-10 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <AppLogo
                logoUrl={app.logo_url}
                platform={app.platform}
                appName={app.app_name}
              />
            </div>
            <p className="text-sm font-medium break-words line-clamp-2">
              {app.app_name}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: columnIds.COUNTRY,
      id: columnIds.COUNTRY,
      size: 180,
      header: t("appCountryWiseReports.columns.country"),
      cell: ({ row }) => {
        if (!row.original.country) {
          return "-";
        }
        return (
          <CountryDisplay
            countryCode={row.original.country_code}
            countryName={row.original.country}
            onClick={
              hasCountryCostReadPermission
                ? () => {
                  onCampaignCostClick?.(
                    row.original.country_code,
                    row.original.country,
                    row.original.app_package_id
                  );
                }
                : undefined
            }
          />
        );
      },
    },
    {
      accessorKey: columnIds.ACCOUNT,
      id: columnIds.ACCOUNT,
      size: 150,
      header: t("appCountryWiseReports.columns.account"),
      cell: ({ row }) => (
        <div
          className="relative text-xs sm:text-sm break-words whitespace-normal line-clamp-2 text-ellipsis"
          title={row.original.user_name || ""}
        >
          {row.original.user_name || "-"}
        </div>
      ),
    },
    {
      accessorKey: columnIds.ESTIMATE_REVENUE,
      id: columnIds.ESTIMATE_REVENUE,
      header: t("appCountryWiseReports.columns.estimateRevenue"),
      meta: {
        headerAlign: "right",
      },
      cell: ({ row }) => {
        const value = row.original.estimate_revenue;
        const networkBifurcation = row.original.network_bifurcation;
        const entries = getNetworkWiseRevenue(networkBifurcation || {});
        const hasNetworkBifurcation =
          !!networkBifurcation &&
          Object.keys(networkBifurcation || {}).length > 0 &&
          entries.length >= 2;
        return (
          <div className="w-full flex items-center justify-end relative">
            {hasNetworkBifurcation ? (
              <Tooltip
                placement="right"
                content={
                  <div className="flex flex-col gap-2 min-w-56">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-6">
                        <span className="text-xs font-semibold">Network:</span>
                        <span className="text-xs tabular-nums text-emerald-400 font-semibold">
                          {Number.isFinite(Number(entries?.[0]?.amount))
                            ? formatNumber(Number(entries?.[0]?.amount))
                            : "0.00"}
                        </span>
                      </div>

                      {entries.slice(1).length > 0 ? (
                        <div className="pl-3 border-l-2 border-emerald-500 flex flex-col gap-1">
                          {entries.slice(1).map((e) => (
                            <div
                              key={e.network}
                              className="flex items-center justify-between gap-6"
                            >
                              <span className="text-xs">
                                {String(e.network).endsWith(":")
                                  ? String(e.network).slice(0, -1)
                                  : String(e.network)}
                              </span>
                              <span className="text-xs tabular-nums">
                                {Number.isFinite(Number(e.amount))
                                  ? formatNumber(Number(e.amount))
                                  : "0.00"}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                }
                arrow={true}
              >
                <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
                  <span className="text-sm whitespace-nowrap text-right block">
                    {isNaN(value) ? "0.00" : formatNumber(value)}
                  </span>
                  <span className="inline-flex w-[14px] justify-end">
                    <Info
                      size={14}
                      className="text-gray-400 hover:text-gray-600 cursor-help"
                    />
                  </span>
                </div>
              </Tooltip>
            ) : (
              <div className="inline-flex items-center justify-end gap-1 tabular-nums w-full">
                <span className="text-sm whitespace-nowrap text-right block">
                  {isNaN(value) ? "0.00" : formatNumber(value)}
                </span>
                <span className="inline-flex w-[14px] justify-end invisible">
                  <Info size={14} className="text-gray-400" />
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: columnIds.IN_APP_PURCHASES_OR_SUBSCRIPTIONS,
      accessorKey: "developer_proceeds",
      header: () => (
        <div className="flex items-center justify-end gap-1 relative">
          <span>{t("appCountryWiseReports.columns.inAppPurchasesOrSubscriptions")}</span>
          <Tooltip content="This is Net Amount">
            <Info size={14} className="text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
      ),
      size: 170,
      meta: { headerAlign: "right" },
      cell: ({ row }) => {
        const inAppRevenue = row.original.developer_proceeds;
        return (
          <span className="text-xs sm:text-sm whitespace-nowrap text-right block">
            {isNaN(inAppRevenue) ? "0.00" : formatNumber(inAppRevenue)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.COST_DOLLAR,
      id: columnIds.COST_DOLLAR,
      header: t("appCountryWiseReports.columns.totalCost"),
      meta: {
        headerAlign: "right",
      },
      cell: ({ row }) => {
        const value = row.original.without_tax?.total_cost_usd;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            {isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.COST_INR,
      id: columnIds.COST_INR,
      header: t("appCountryWiseReports.columns.totalCostInr"),
      meta: {
        headerAlign: "right",
      },
      cell: ({ row }) => {
        const value = row.original.without_tax?.total_campaign_cost_inr;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            {isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.NET_DOLLAR,
      id: columnIds.NET_DOLLAR,
      header: t("appCountryWiseReports.columns.netDollar"),
      meta: {
        headerAlign: "right",
      },
      cell: ({ row }) => {
        const value = row.original.without_tax?.total_net;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            {isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.PERCENTAGE,
      id: columnIds.PERCENTAGE,
      header: t("appCountryWiseReports.columns.percentage"),
      size: 100,
      meta: {
        headerAlign: "right",
      },
      cell: ({ row }) => {
        const percentage = row.original.without_tax?.net_percentage;
        let color = "gray";

        const percentageValue = percentage * 100; // Multiply by 100 before comparison
        const [minThreshold, maxThreshold] = growthIndicatorRange;
        if (percentageValue < minThreshold) {
          color = "failure";
        } else if (
          percentageValue >= minThreshold &&
          percentageValue <= maxThreshold
        ) {
          color = "warning";
        } else if (percentageValue > maxThreshold) {
          color = "success";
        }

        return (
          <Badge color={color} size="sm" className="whitespace-nowrap ml-auto">
            {isNaN(percentage) ? "0.00" : formatNumber(percentage * 100)}%
          </Badge>
        );
      },
    },

    // WithTax columns
    {
      accessorKey: columnIds.COST_DOLLAR_WITH_TAX,
      id: columnIds.COST_DOLLAR_WITH_TAX,
      header: t("appCountryWiseReports.columns.totalCostDollarWithTax"),
      meta: { headerAlign: "right" },
      cell: ({ row }) => {
        const value = row.original.with_tax?.total_cost_usd;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            {isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.COST_INR_WITH_TAX,
      id: columnIds.COST_INR_WITH_TAX,
      header: t("appCountryWiseReports.columns.totalCostWithTax"),
      meta: { headerAlign: "right" },
      cell: ({ row }) => {
        const value = row.original.with_tax?.total_campaign_cost_inr;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            {isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.NET_DOLLAR_WITH_TAX,
      id: columnIds.NET_DOLLAR_WITH_TAX,
      header: t("appCountryWiseReports.columns.netDollarWithTax"),
      meta: { headerAlign: "right" },
      cell: ({ row }) => {
        const value = row.original.with_tax?.total_net;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            {isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
    },
    {
      accessorKey: columnIds.PERCENTAGE_WITH_TAX,
      id: columnIds.PERCENTAGE_WITH_TAX,
      header: t("appCountryWiseReports.columns.percentageWithTax"),
      meta: { headerAlign: "right" },
      size: 160,
      cell: ({ row }) => {
        const percentage = row.original.with_tax?.net_percentage;
        let color = "gray";
        const percentageValue = percentage * 100; // Multiply by 100 before comparison
        const [minThreshold, maxThreshold] = growthIndicatorRange;
        if (percentageValue < minThreshold) {
          color = "failure";
        } else if (
          percentageValue >= minThreshold &&
          percentageValue <= maxThreshold
        ) {
          color = "warning";
        } else if (percentageValue > maxThreshold) {
          color = "success";
        }
        return (
          <Badge color={color} size="sm" className="whitespace-nowrap ml-auto">
            {isNaN(percentage) ? "0.00" : formatNumber(percentage * 100)}%
          </Badge>
        );
      },
    },
    {
      id: columnIds.LAST_BID_CURRENT_BID_RUPEE,
      accessorKey: "bid_data",
      header: t("appCountryWiseReports.columns.lastBidCurrentBid"),
      size: 150,
      cell: ({ row }) => {
        return <BidDataCell bidData={row.original.bid_data || []} t={t} />;
      },
    },
    ...(hasAppChartReadPermission || hasCampaignBidManagementPermission
      ? [
        {
          id: columnIds.ACTIONS,
          header: t("apps.columns.actions"),
          size: 120,
          cell: ({ row }: { row: Row<AdmobApp> }) => {
            const app = row.original;
            return (
              <div className="flex gap-2 relative w-full">
                {hasAppChartReadPermission && (
                  <Tooltip content={t("apps.appList.revenueChartTooltip")}>
                    <Button
                      size="xs"
                      color="blue"
                      outline
                      className="p-[2px]"
                      onClick={() => onChartClick?.(app)}
                    >
                      <ChartBar className="h-5" size={16} />
                    </Button>
                  </Tooltip>
                )}
                {hasCampaignBidManagementPermission &&
                  app?.bid_data &&
                  app?.bid_data?.length > 0 && (
                    <Tooltip content={t("apps.appList.bidManagementTooltip")}>
                      <Button
                        size="xs"
                        outline
                        color="indigo"
                        onClick={() => {
                          try {
                            if (app?.bid_data && app?.bid_data?.length > 0) {
                              navigate(
                                `/app-country-reports/${app.app_slug}/bid-management?country-code=${app.country_code}`
                              );
                            }
                          } catch (error) {
                            console.error("Error encoding bid data:", error);
                          }
                        }}
                        className="p-[2px]"
                      >
                        <Megaphone className="h-5" size={16} />
                      </Button>
                    </Tooltip>
                  )}
              </div>
            );
          },
        },
      ]
      : []),
  ];

  // Filter columns based on enabledColumnIds
  const filteredColumns = allColumns.filter((col) => {
    const id = typeof col.id === "string" ? col.id : "";
    const isEnabled = enabledColumnIds.includes(id);
    return isEnabled;
  });
  return filteredColumns;
};
